import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { handleOAuthCallback } from "../lib/auth";
import { AuthScreen, AuthSpinner, AuthButton } from "../components/AuthScreen";

type Phase = "loading" | "success" | "error";

export function CallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [phase, setPhase] = useState<Phase>("loading");
  const handledRef = useRef(false);

  useEffect(() => {
    // StrictMode runs effects twice in dev — guard against double-consuming the auth code.
    // Deliberately NO `mounted` flag: StrictMode's first cleanup would flip it false
    // before the token exchange resolves, and the guarded second run never resets it,
    // so every dev sign-in hung forever on "Signing you in…". React 18 makes a late
    // setState on an unmounted component a silent no-op, so the flag bought nothing.
    if (handledRef.current) return;
    handledRef.current = true;

    const oauthError = searchParams.get("error");
    if (oauthError) {
      const desc = searchParams.get("error_description") || oauthError;
      setError(`Sign-in failed: ${desc}`);
      setPhase("error");
      return;
    }

    const code = searchParams.get("code");
    if (!code) {
      setError("No authorization code in callback URL.");
      setPhase("error");
      return;
    }

    const state = searchParams.get("state");
    handleOAuthCallback(code, state)
      .then(() => {
        setPhase("success");
        // FULL-page load (not client-side navigate) so ProfileProvider remounts
        // and re-reads the now-present session. A client navigate leaves the
        // auth context stale → RequireAuth bounces back to /login (the old loop).
        setTimeout(() => window.location.replace("/today"), 500);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
        setPhase("error");
      });
  }, [searchParams, navigate]);

  return (
    <AuthScreen>
      {phase === "error" ? (
        <>
          <div style={{ fontSize: 16, fontWeight: 500, color: "#e7ebee" }}>Sign-in failed</div>
          <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#aeb6bd" }}>{error}</div>
          <AuthButton onClick={() => navigate("/login", { replace: true })}>Back to sign in</AuthButton>
        </>
      ) : (
        <>
          <AuthSpinner />
          <div style={{ fontSize: 15, fontWeight: 500, color: "#e7ebee" }}>
            {phase === "success" ? "Welcome back — taking you in…" : "Signing you in…"}
          </div>
        </>
      )}
    </AuthScreen>
  );
}
